import { Box } from "@mui/material";
import { CatalogBuilder } from "../../../features/catalogs/catalog.builder";
import { DesignItemRef } from "../../../features/design/design.types";
import {
  LayoutProductSlots,
  bindSlotProducts,
} from "../../../features/layouts/layout.product-slots";
import { slotColor } from "../../../features/layouts/layout.slot-colors";
import { ProductRef } from "../../../features/products/product";

/**
 * The real products bound to a layout's slots for the in-editor preview + the
 * layers labels. Slot numbers are assigned in the same order `injectLayoutItems`
 * shifts products, so slot k -> products[k - 1] (see `bindSlotProducts`).
 */
export type SlotBinding = {
  /** the sampled products, in slot order (may be shorter than slots.total) */
  products: ProductRef[];
  productBySlot: Map<number, ProductRef>;
  /** Product Key -> slot number, for keyed elements */
  keyToSlot: Map<string, number>;
  /** bound product id -> slot number */
  slotByProductId: Map<string, number>;
  /** how many products the account has in total (drives the sample-cycle control) */
  totalProducts: number;
};

/**
 * Pick the sample products for the preview: the first N of the account's products
 * (N = distinct slot count), rotated by `sampleOffset` so the user can cycle through
 * different real data. Fewer products than slots -> the tail slots stay unbound.
 *
 * `overrides` (slot -> product id) let the user hand-pick a specific product per slot
 * in the Products tab. These are PREVIEW-ONLY sample choices — they never persist to
 * the layout (a layout defines slots, it doesn't own products). An override wins over
 * the auto sample for its slot; other slots keep the rotated default.
 */
export const computeSlotBinding = (
  builder: CatalogBuilder,
  slots: LayoutProductSlots,
  sampleOffset: number,
  overrides?: Map<number, string>
): SlotBinding => {
  const productMap = builder?.data?.productDb?.productMap as
    | Map<string, ProductRef>
    | undefined;
  const all = Array.from(productMap?.values() ?? []) as ProductRef[];
  const n = slots.total;
  const products: ProductRef[] = [];
  if (all.length && n > 0) {
    for (let i = 0; i < n && i < all.length; i++) {
      products.push(all[(sampleOffset + i) % all.length]);
    }
  }

  const productBySlot = bindSlotProducts(slots, products);

  if (overrides && productMap) {
    overrides.forEach((productId, slot) => {
      if (slot < 1 || slot > n) return;
      const p = productMap.get(productId);
      if (p) productBySlot.set(slot, p);
    });
  }

  const keyToSlot = new Map<string, number>();
  slots.slotById.forEach((slot, itemId) => {
    const key = slots.keyById.get(itemId);
    if (key && !keyToSlot.has(key)) keyToSlot.set(key, slot);
  });

  const slotByProductId = new Map<string, number>();
  productBySlot.forEach((p, slot) => slotByProductId.set(p.product.id, slot));

  return {
    products,
    productBySlot,
    keyToSlot,
    slotByProductId,
    totalProducts: all.length,
  };
};

/** Slot number for a bound PREVIEW item (a catalog item), by its key or product. */
export const slotOfCatalogItem = (
  item: DesignItemRef,
  binding: SlotBinding
): number | undefined => {
  const di = item.di as { Title?: string; ItemId?: string };
  if (di.Title && binding.keyToSlot.has(di.Title))
    return binding.keyToSlot.get(di.Title);
  if (di.ItemId && binding.slotByProductId.has(di.ItemId))
    return binding.slotByProductId.get(di.ItemId);
  return undefined;
};

type SlotOverlayProps = {
  slot: number;
  productName?: string;
  hovered: boolean;
  /** preview = whole element is a hover target; source = only the corner dot is */
  interactive: boolean;
  /** preview shows the product-name badge; source shows just the colour dot */
  showLabel: boolean;
  onEnter: () => void;
  onLeave: () => void;
};

/**
 * Per-slot colour badge + highlight ring drawn over an element, in both the
 * Preview and Source boards. The ring reflects `hovered` (driven by the shared
 * hoveredSlot); the corner badge/dot is always a hover target so the board can
 * also SET the hover. In source mode the outer box is pointer-events:none so it
 * never blocks dragging/selecting — only the small dot captures the mouse.
 */
export const SlotOverlay = ({
  slot,
  productName,
  hovered,
  interactive,
  showLabel,
  onEnter,
  onLeave,
}: SlotOverlayProps) => {
  const color = slotColor(slot);
  return (
    <Box
      onMouseEnter={interactive ? onEnter : undefined}
      onMouseLeave={interactive ? onLeave : undefined}
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 6,
        pointerEvents: interactive ? "auto" : "none",
        borderRadius: "inherit",
        boxShadow: hovered ? `inset 0 0 0 2px ${color}` : "none",
        transition: "box-shadow 120ms ease",
      }}
    >
      <Box
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
        title={productName ? `Product ${slot} - ${productName}` : `Product ${slot}`}
        sx={{
          position: "absolute",
          top: 2,
          left: 2,
          pointerEvents: "auto",
          display: "flex",
          alignItems: "center",
          height: showLabel ? 16 : 10,
          minWidth: 10,
          maxWidth: "85%",
          px: showLabel ? 0.5 : 0,
          borderRadius: showLabel ? 0.75 : "50%",
          bgcolor: color,
          color: "#111",
          fontSize: 10,
          fontWeight: 700,
          lineHeight: 1,
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
          boxShadow: "0 0 0 1px rgba(0,0,0,0.3)",
          cursor: "default",
        }}
      >
        {showLabel && (productName ?? `P${slot}`)}
      </Box>
    </Box>
  );
};
